import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input, Modal, SegmentedControl } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

type Kind = 'broadcast' | 'sequence';

const KIND_OPTIONS = [
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'sequence', label: 'Sequence' },
] as const;

/**
 * Name + kind, then straight into the builder. The server creates the draft
 * with step 1 and variant A, so there is nothing else to collect here.
 */
export function NewCampaignModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<Kind>('broadcast');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  function close() {
    if (saving) return;
    setName('');
    setKind('broadcast');
    setError(null);
    setNameError(null);
    onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Give the campaign a name.');
      return;
    }
    setSaving(true);
    setError(null);
    setNameError(null);
    try {
      const created = await api.createMarketingCampaign({ name: trimmed, kind });
      navigate(`/marketing/campaigns/${created.data.id}`);
    } catch (caught) {
      if (caught instanceof ApiError) {
        const fields = caught.fieldErrors;
        if (fields.name) setNameError(fields.name);
        else setError(caught.formMessage ?? caught.message);
      } else {
        setError('Could not reach the server. Nothing was created.');
      }
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="New campaign"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="new-campaign-form" loading={saving}>
            Create and open
          </Button>
        </>
      }
    >
      <form id="new-campaign-form" onSubmit={submit} className="grid gap-4" noValidate>
        {error && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
        <Field label="Name" required error={nameError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              maxLength={200}
              invalid={Boolean(nameError)}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          )}
        </Field>
        <div className="grid gap-1.5">
          <span aria-hidden="true" className="text-xs font-medium text-fg-secondary">
            Kind
          </span>
          <SegmentedControl<Kind>
            label="Kind"
            value={kind}
            options={KIND_OPTIONS}
            onChange={setKind}
            disabled={saving}
          />
        </div>
      </form>
    </Modal>
  );
}
